import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorForm = ({ eventId = 'kontakt-fest-2026', eventName = 'KONTAKT Fest', pricingConfig = null }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState('');
  const [isLoadingAres, setIsLoadingAres] = useState(false);
  const [ico, setIco] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);

  // Default pricing for KONTAKT Fest (2-day event)
  const defaultPricing = {
    isMultiDay: true,
    mainSeller: { daily: 15000, singleDay: 15000 },
    otherSeller: { daily: 7000, singleDay: 7000 },
    electricity: {
      '230V max 3,5 kW': 1750,
      '16A/380V (3f) - 3,5kW až 6kW': 3300,
      '32A/380V (3f) - 6kW až 12 kW': 4500,
      'Nepotřebuji': 0
    },
    deposit: 5000
  };

  // For Den ZK (1-day event)
  const denZKPricing = {
    isMultiDay: false,
    mainSeller: { singleDay: 6000 },
    otherSeller: { singleDay: 3000 },
    electricity: {
      'DO 3KW': 1750,
      '3KW až 6KW': 3300,
      'nad 6KW': 4500,
      'Nepotřebuji': 0
    },
    deposit: 0
  };

  // Use custom pricing if provided, otherwise use event-specific pricing
  const pricing = pricingConfig || (eventId === 'den-zk-2026' ? denZKPricing : defaultPricing);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    vendorName: '',
    vatPayer: '',
    invoiceDetails: '',
    website: '',
    description: '',
    menu: '',
    stallSize: '',
    electricConnection: '',
    notes: '',
    photos: [],
    eventDays: pricing.isMultiDay ? '' : 'jednodenni',  // For single-day events, default to jednodenni
    vendorType: '',  // hlavni-prodej nebo ostatni-prodejci
    eventId: eventId  // Add eventId to form data
  });

  // Kalkulace celkové částky
  const calculateTotal = () => {
    // Pro den-zk-2026 není potřeba electricConnection (elektřina je v ceně)
    const needsElectricConnection = eventId !== 'den-zk-2026' && pricing.electricity && Object.keys(pricing.electricity).length > 0;
    
    if (!formData.vendorType || (needsElectricConnection && !formData.electricConnection)) {
      return null;
    }

    // For multi-day events, also need eventDays
    if (pricing.isMultiDay && !formData.eventDays) {
      return null;
    }

    // Cena za nájem podle typu a dnů
    let rentTotal = 0;
    if (pricing.isMultiDay) {
      const dailyRate = formData.vendorType === 'hlavni-prodej' 
        ? pricing.mainSeller.daily 
        : pricing.otherSeller.daily;
      const days = formData.eventDays === 'oboudenni' ? 2 : 1;
      rentTotal = dailyRate * days;
    } else {
      // Single day event
      rentTotal = formData.vendorType === 'hlavni-prodej' 
        ? pricing.mainSeller.singleDay 
        : pricing.otherSeller.singleDay;
    }

    // Cena za energii - jen pokud není zahrnuta v ceně
    const energyCost = (needsElectricConnection && pricing.electricity[formData.electricConnection]) || 0;

    // Vratná kauce
    const deposit = pricing.deposit || 0;

    return {
      rent: rentTotal,
      energy: energyCost,
      deposit: deposit,
      total: rentTotal + energyCost + deposit
    };
  };

  const priceCalc = calculateTotal();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const loadFromAres = async () => {
    if (!ico || ico.length < 8) {
      toast({
        title: "Chyba",
        description: "Zadejte platné IČO (8 číslic)",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingAres(true);

    try {
      const response = await axios.get(`${BACKEND_URL}/api/ares/company/${ico}`);
      
      if (response.data.success) {
        // Automaticky vyplň formulář daty z ARES
        setFormData(prev => ({
          ...prev,
          vatPayer: response.data.vatPayer,
          invoiceDetails: response.data.invoiceDetails
        }));

        toast({
          title: "Údaje načteny!",
          description: `Firma ${response.data.nazev} byla nalezena v registru ARES`,
        });
      }
    } catch (error) {
      console.error('ARES error:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.detail || "Nepodařilo se načíst údaje z ARES",
        variant: "destructive",
      });
    } finally {
      setIsLoadingAres(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 5) {
      toast({
        title: "Příliš mnoho fotek",
        description: "Maximálně můžete nahrát 5 fotografií",
        variant: "destructive",
      });
      return;
    }

    // Vytvoř preview
    const previews = files.map(file => URL.createObjectURL(file));
    setPhotosPreviews(previews);
    setPhotos(files);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = photosPreviews.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    setPhotosPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Nejdřív nahraj fotky, pokud jsou
      let photoUrls = [];
      if (photos.length > 0) {
        setIsUploadingPhotos(true);
        const formDataPhotos = new FormData();
        photos.forEach(photo => {
          formDataPhotos.append('files', photo);
        });

        const uploadResponse = await axios.post(`${BACKEND_URL}/api/upload/vendor-photos`, formDataPhotos, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (uploadResponse.data.success) {
          photoUrls = uploadResponse.data.files;
          toast({
            title: "Fotky nahrány!",
            description: `Nahráno ${photoUrls.length} fotografií`,
          });
        }
        setIsUploadingPhotos(false);
      }

      // Přidej URL fotek, totalPrice a eventId do formData
      const priceCalculation = calculateTotal();
      const submissionData = {
        ...formData,
        photos: photoUrls,
        totalPrice: priceCalculation ? priceCalculation.total : null,
        eventId: eventId  // ID aktuálního festivalu (dynamicky podle props)
      };

      // Real API call
      const response = await axios.post(`${BACKEND_URL}/api/vendor-applications`, submissionData);
      
      if (response.data) {
        setIsSubmitted(true);
        setRegistrationId(response.data.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast({
          title: "Přihláška odeslána!",
          description: "Brzy vás budeme kontaktovat s dalšími informacemi.",
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.detail || "Nepodařilo se odeslat přihlášku. Zkuste to prosím znovu.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploadingPhotos(false);
    }
  };

  if (isSubmitted) {
    const statusUrl = `${window.location.origin}/status/${registrationId}`;
    
    return (
      <Card className="border-2 border-orange-300 bg-orange-50">
        <CardContent className="p-12 text-center">
          <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-slate-900 mb-4">Děkujeme za přihlášku!</h3>
          <p className="text-lg text-slate-700 mb-6">
            Vaše registrace byla úspěšně odeslána. Brzy vás budeme kontaktovat s dalšími informacemi.
          </p>
          
          <div className="bg-white border-2 border-orange-200 rounded-lg p-6 mb-6 max-w-2xl mx-auto">
            <p className="text-sm text-slate-600 mb-2">📋 Registrační číslo:</p>
            <p className="text-2xl font-bold text-orange-500 mb-4">{registrationId}</p>
            
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-blue-900 mb-3">
                🔗 Zkontrolujte stav Vaší přihlášky:
              </p>
              <a 
                href={statusUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-mono block mb-3"
              >
                {statusUrl}
              </a>
              <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                💡 Tento link si uložte! Můžete se k němu kdykoliv vrátit a zkontrolovat, 
                zda byla Vaše přihláška schválena. Po schválení zde najdete také důležité 
                informace o platbě a termínech.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 max-w-2xl mx-auto text-left">
            <p className="text-sm text-amber-900">
              <strong>ℹ️ Co bude dál:</strong><br/>
              1. Vaše přihláška čeká na schválení organizátorem<br/>
              2. O výsledku se dozvíte prostřednictvím odkazu výše<br/>
              3. Po schválení Vám přijde zálohová faktura<br/>
              4. Po úhradě faktury s Vámi závazně počítáme jako s účastníkem
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-slate-200 shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl">Registrační formulář</CardTitle>
        <CardDescription>
          Vyplňte prosím všechny povinné údaje. Odesláním formuláře se závazně přihlašujete na {eventName}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Informační box s cenami */}
        <div className={`mb-8 p-6 ${eventId === 'den-zk-2026' ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400' : 'bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200'} rounded-lg`}>
          <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            💰 Ceník a podmínky
          </h3>
          <div className="space-y-3 text-slate-700">
            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="font-semibold">
                {eventId === 'den-zk-2026' ? 'Food trucky/stánky s jídlem (do 6x3m):' : 'Food trucky/stánky s jídlem (do 6x3m):'}
              </span>
              <span className={`${eventId === 'den-zk-2026' ? 'text-gray-900' : 'text-orange-600'} font-bold text-lg`}>
                {pricing.isMultiDay 
                  ? `${(pricing.mainSeller.daily || pricing.mainSeller.singleDay).toLocaleString()} Kč/den`
                  : `${pricing.mainSeller.singleDay.toLocaleString()} Kč`
                }
              </span>
            </div>
            {eventId === 'den-zk-2026' && (
              <p className="text-xs text-gray-600 italic -mt-2 ml-3">Cena včetně elektřiny</p>
            )}
            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="font-semibold">
                {eventId === 'den-zk-2026' ? 'Sladké, pochutiny (max. 6x3m):' : 'Ostatní prodejci (max. 6x3m):'}
              </span>
              <span className={`${eventId === 'den-zk-2026' ? 'text-gray-900' : 'text-orange-600'} font-bold text-lg`}>
                {pricing.isMultiDay 
                  ? `${(pricing.otherSeller.daily || pricing.otherSeller.singleDay).toLocaleString()} Kč/den`
                  : `${pricing.otherSeller.singleDay.toLocaleString()} Kč`
                }
              </span>
            </div>
            {pricing.deposit > 0 && (
              <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                <span className="font-semibold">Vratná kauce:</span>
                <span className={`${eventId === 'den-zk-2026' ? 'text-gray-900' : 'text-orange-600'} font-bold text-lg`}>{pricing.deposit.toLocaleString()} Kč</span>
              </div>
            )}
            {eventId !== 'den-zk-2026' && pricing.electricity && Object.keys(pricing.electricity).length > 0 && (
              <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                <span className="font-semibold">Energie (celá akce):</span>
                <span className="text-orange-600 font-bold text-lg">
                  {Math.min(...Object.values(pricing.electricity).filter(v => v > 0)).toLocaleString()} - {Math.max(...Object.values(pricing.electricity)).toLocaleString()} Kč
                </span>
              </div>
            )}
            <p className="text-sm text-slate-600 italic mt-2">* Ceny jsou uvedeny bez DPH</p>
          </div>
          
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-900">
              <strong>⚠️ Důležité pravidla:</strong><br/>
              • Nápoje zajišťuje výhradně pořadatel
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Základní informace */}
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold text-slate-900 pb-2 border-b-2 ${eventId === 'den-zk-2026' ? 'border-yellow-400' : 'border-orange-200'}`}>
              Základní informace
            </h3>

            {/* Výběr dnů akce - POVINNÉ - Only show for multi-day events */}
            {pricing.isMultiDay && (
              <div className="space-y-3 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <Label className="text-lg font-semibold text-slate-900">
                  O jaké dny máte zájem? *
                </Label>
                <RadioGroup
                  name="eventDays"
                  value={formData.eventDays}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, eventDays: value }))}
                  required
                  className="relative z-10"
                >
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-slate-200 hover:border-blue-400 transition-colors">
                    <RadioGroupItem value="oboudenni" id="days-both" className="relative z-10 mt-1" />
                    <div>
                      <Label htmlFor="days-both" className="font-bold text-base cursor-pointer">
                        Dva dny (sobota + neděle)
                      </Label>
                      <p className="text-sm text-slate-600 mt-1">
                        13. - 14. června 2026 | Plný prodejní potenciál
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-slate-200 hover:border-orange-400 transition-colors">
                    <RadioGroupItem value="sobota-open-stage" id="days-saturday" className="relative z-10 mt-1" />
                    <div>
                      <Label htmlFor="days-saturday" className="font-bold text-base cursor-pointer">
                        Pouze sobota - Open Stage
                      </Label>
                      <p className="text-sm text-slate-600 mt-1">
                        13. června 2026 | Rychlovýdejní sortiment (nudle, gyros apod.)
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Typ prodejce - POVINNÉ */}
            <div className={`space-y-3 p-4 ${eventId === 'den-zk-2026' ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-purple-50 border-2 border-purple-300'} rounded-lg`}>
              <Label className="text-lg font-semibold text-slate-900">
                Typ prodejce *
              </Label>
              <RadioGroup
                name="vendorType"
                value={formData.vendorType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, vendorType: value }))}
                required
                className="relative z-10"
              >
                <div className={`flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-slate-200 ${eventId === 'den-zk-2026' ? 'hover:border-yellow-400' : 'hover:border-orange-400'} transition-colors`}>
                  <RadioGroupItem value="hlavni-prodej" id="type-main" className="relative z-10 mt-1" />
                  <div>
                    <Label htmlFor="type-main" className="font-bold text-base cursor-pointer">
                      Hlavní prodej - Food trucky/stánky s jídlem
                    </Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {pricing.isMultiDay 
                        ? `${(pricing.mainSeller.daily || pricing.mainSeller.singleDay).toLocaleString()} Kč/den`
                        : `${pricing.mainSeller.singleDay.toLocaleString()} Kč`
                      } | Stánek do 6x3m
                    </p>
                  </div>
                </div>
                <div className={`flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-slate-200 ${eventId === 'den-zk-2026' ? 'hover:border-yellow-400' : 'hover:border-purple-400'} transition-colors`}>
                  <RadioGroupItem value="ostatni-prodejci" id="type-other" className="relative z-10 mt-1" />
                  <div>
                    <Label htmlFor="type-other" className="font-bold text-base cursor-pointer">
                      Ostatní prodejci - Sladké, pochutiny, výrobky
                    </Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {pricing.isMultiDay 
                        ? `${(pricing.otherSeller.daily || pricing.otherSeller.singleDay).toLocaleString()} Kč/den`
                        : `${pricing.otherSeller.singleDay.toLocaleString()} Kč`
                      } | Stánek max. 6x3m
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="vas@email.cz"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Jméno a příjmení *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jan Novák"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+420 XXX XXX XXX"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendorName">Název prodejce *</Label>
              <Input
                id="vendorName"
                name="vendorName"
                type="text"
                placeholder="Např. Food Truck XYZ"
                value={formData.vendorName}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>
          </div>

          {/* Fakturační údaje */}
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold text-slate-900 pb-2 border-b-2 ${eventId === 'den-zk-2026' ? 'border-yellow-400' : 'border-orange-200'}`}>
              Fakturační údaje
            </h3>

            {/* IČO s tlačítkem načíst z ARES */}
            <div className="space-y-2">
              <Label htmlFor="ico">IČO *</Label>
              <div className="flex gap-2">
                <Input
                  id="ico"
                  name="ico"
                  type="text"
                  placeholder="12345678"
                  value={ico}
                  onChange={(e) => setIco(e.target.value)}
                  className="border-slate-300"
                  maxLength={8}
                />
                <Button
                  type="button"
                  onClick={loadFromAres}
                  disabled={isLoadingAres || !ico}
                  className="bg-blue-500 hover:bg-blue-600 text-white whitespace-nowrap"
                >
                  {isLoadingAres ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Načítám...
                    </>
                  ) : (
                    'Načíst z ARES'
                  )}
                </Button>
              </div>
              <p className="text-sm text-slate-500">Zadejte IČO a klikněte na "Načíst z ARES" pro automatické vyplnění</p>
            </div>

            <div className="space-y-3">
              <Label>Plátce DPH *</Label>
              <RadioGroup
                name="vatPayer"
                value={formData.vatPayer}
                onValueChange={(value) => setFormData(prev => ({ ...prev, vatPayer: value }))}
                required
                className="relative z-10"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ano" id="vat-yes" className="relative z-10" />
                  <Label htmlFor="vat-yes" className="font-normal cursor-pointer">Ano (vyplňte DIČ v fakturačních údajích)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ne" id="vat-no" className="relative z-10" />
                  <Label htmlFor="vat-no" className="font-normal cursor-pointer">Ne</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoiceDetails">
                Fakturační údaje (název subjektu, IČ, DIČ, fakturační adresa) *
              </Label>
              <Textarea
                id="invoiceDetails"
                name="invoiceDetails"
                placeholder="Např:&#10;Název: Food Truck s.r.o.&#10;IČ: 12345678&#10;DIČ: CZ12345678&#10;Adresa: Hlavní 123, 123 45 Praha"
                value={formData.invoiceDetails}
                onChange={handleInputChange}
                required
                rows={5}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Vyplňte kompletní údaje dle registru</p>
            </div>
          </div>

          {/* Kalkulace celkové částky */}
          {priceCalc && (
            <div className={`p-6 ${eventId === 'den-zk-2026' ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300'} rounded-lg`}>
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                💰 Kalkulace celkové částky
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                  <span className="text-slate-700">
                    Nájem ({formData.vendorType === 'hlavni-prodej' 
                      ? (pricing.mainSeller.daily || pricing.mainSeller.singleDay).toLocaleString() 
                      : (pricing.otherSeller.daily || pricing.otherSeller.singleDay).toLocaleString()
                    } Kč{pricing.isMultiDay ? ` × ${formData.eventDays === 'oboudenni' ? '2 dny' : '1 den'}` : ''}
                    {eventId === 'den-zk-2026' ? ' včetně elektřiny' : ''}):
                  </span>
                  <span className="font-bold text-lg">{priceCalc.rent.toLocaleString()} Kč</span>
                </div>
                
                {/* Zobrazit energii pouze pokud není zahrnuta v ceně */}
                {eventId !== 'den-zk-2026' && priceCalc.energy > 0 && (
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-slate-700">Energie (celá akce):</span>
                    <span className="font-bold text-lg">{priceCalc.energy.toLocaleString()} Kč</span>
                  </div>
                )}
                
                {priceCalc.deposit > 0 && (
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-slate-700">Vratná kauce:</span>
                    <span className="font-bold text-lg">{priceCalc.deposit.toLocaleString()} Kč</span>
                  </div>
                )}
              </div>
              
              <div className={`p-4 ${eventId === 'den-zk-2026' ? 'bg-black' : 'bg-green-600'} rounded-lg text-white`}>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">CELKEM K ÚHRADĚ:</span>
                  <span className="text-3xl font-bold">{priceCalc.total.toLocaleString()} Kč bez DPH</span>
                </div>
                <p className={`text-sm ${eventId === 'den-zk-2026' ? 'text-gray-300' : 'text-green-100'} mt-2`}>* Všechny uvedené ceny jsou bez DPH</p>
              </div>
            </div>
          )}

          {/* Informace o prodejci */}
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold text-slate-900 pb-2 border-b-2 ${eventId === 'den-zk-2026' ? 'border-yellow-400' : 'border-orange-200'}`}>
              Informace o prodejci
            </h3>

            <div className="space-y-2">
              <Label htmlFor="website">WWW / FB stránky prodejce *</Label>
              <Input
                id="website"
                name="website"
                type="text"
                placeholder="https://www.vase-stranky.cz nebo facebook.com/vasestranky"
                value={formData.website}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Napište o sobě pár vět *
              </Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Krátký popis vašeho konceptu, který použijeme k propagaci na sociálních sítích..."
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Bude použito k propagaci na sociálních sítích</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="menu">
                Předpokládané menu *
              </Label>
              <Textarea
                id="menu"
                name="menu"
                placeholder="Např:&#10;- Burger s hovězím masem&#10;- Vegetariánský burger&#10;- Hranolky&#10;- Domácí limonády"
                value={formData.menu}
                onChange={handleInputChange}
                required
                rows={5}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Ceny nemusíte uvádět. Bude použito k propagaci.</p>
            </div>
          </div>

          {/* Technické informace */}
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold text-slate-900 pb-2 border-b-2 ${eventId === 'den-zk-2026' ? 'border-yellow-400' : 'border-orange-200'}`}>
              Technické údaje
            </h3>

            <div className="space-y-2">
              <Label htmlFor="stallSize">Rozměr stánku *</Label>
              <Input
                id="stallSize"
                name="stallSize"
                type="text"
                placeholder="Např. 3x3m nebo 6x3m"
                value={formData.stallSize}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            {/* Elektrická přípojka */}
            {pricing.electricity && Object.keys(pricing.electricity).length > 0 && (
              <div className="space-y-3">
                <Label>Jakou elektrickou přípojku potřebujete? *</Label>
                {eventId === 'den-zk-2026' && (
                  <p className="text-sm text-gray-600 italic">Elektřina je zahrnuta v ceně, potřebujeme však vědět jaký příkon budete potřebovat.</p>
                )}
                <RadioGroup
                  name="electricConnection"
                  value={formData.electricConnection}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, electricConnection: value }))}
                  required
                  className="relative z-10"
                >
                  {Object.keys(pricing.electricity).map((option, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <RadioGroupItem value={option} id={`elec-${idx}`} className="relative z-10" />
                      <Label htmlFor={`elec-${idx}`} className="font-normal cursor-pointer">
                        {option} 
                        {eventId !== 'den-zk-2026' && pricing.electricity[option] > 0 ? ` (${pricing.electricity[option].toLocaleString()} Kč)` : ''}
                        {eventId === 'den-zk-2026' && pricing.electricity[option] === 0 && option !== 'Nepotřebuji' ? ' (zdarma)' : ''}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>

          {/* Dodatečné informace */}
          <div className="space-y-4">
            <h3 className={`text-xl font-semibold text-slate-900 pb-2 border-b-2 ${eventId === 'den-zk-2026' ? 'border-yellow-400' : 'border-orange-200'}`}>
              Dodatečné informace
            </h3>

            {/* Fotografie upload */}
            <div className="space-y-3">
              <Label htmlFor="photos">Reprezentativní fotografie (max 5 ks)</Label>
              <Input
                id="photos"
                name="photos"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">
                Nahrajte až 5 fotografií vašeho stánku, produktů nebo týmu (max 10MB/foto)
              </p>

              {/* Preview fotek */}
              {photosPreviews.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                  {photosPreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Poznámky</Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Máte-li jakékoli dotazy nebo speciální požadavky, napište nám je zde..."
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="border-slate-300"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t-2 border-slate-200">
            <div className={`${eventId === 'den-zk-2026' ? 'bg-yellow-50 border-yellow-300' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 mb-6`}>
              <p className={`text-sm ${eventId === 'den-zk-2026' ? 'text-gray-900' : 'text-amber-900'}`}>
                <strong>Upozornění:</strong> Odesláním formuláře se závazně přihlašujete na {eventName}. 
                Po zaplacení zálohové faktury je vaše místo 100% rezervováno.
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || isUploadingPhotos}
              className={`w-full ${eventId === 'den-zk-2026' ? 'bg-yellow-400 hover:bg-yellow-500 text-black' : 'bg-orange-500 hover:bg-orange-600 text-white'} text-lg py-6 font-bold rounded-full`}
            >
              {isUploadingPhotos ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Nahrávám fotografie...
                </>
              ) : isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Odesílám přihlášku...
                </>
              ) : (
                'Odeslat přihlášku'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default VendorForm;
